import React, { useState, useEffect } from "react";
import { InputField, DropDownInput, Button } from "@/components";
import { MarkdownEditor, SpaceWrapper } from "../../components";
import { BsArrowLeftShort } from "react-icons/bs";
import { FileUpload } from "../../components";
import { useNavigate, useLocation } from "react-router-dom";
import { useAlert, useFormData } from "@/";
import { useDispatch } from "react-redux";
import { saveBlog, updateBlog } from "../../app/Slices";

const CreateBlogs = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { alert, AlertDialog } = useAlert();
  const dispatch = useDispatch();
  const convertToFormData = useFormData();
  const [isEditing, setIsEditing] = useState(false);
  const [blogId, setBlogId] = useState(null);
  const [formData, setFormData] = useState({
    postTitle: "",
    postImage: "",
    postCategory: "",
    postDescription: "",
  });

  useEffect(() => {
    // Check if we have blog data in location state (from edit button)
    if (location.state && location.state.blog) {
      const blog = location.state.blog;
      setFormData({
        postTitle: blog.title || "",
        postImage: blog.imageUrl || "",
        postCategory: blog.category || "",
        postDescription: blog.content || "",
      });
      setBlogId(blog._id);
      setIsEditing(true);
    }
  }, [location]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleReset = () => {
    setFormData({
      postTitle: "",
      postImage: "",
      postCategory: "",
      postDescription: "",
    });
    if (isEditing) {
      navigate("/blogs");
    }
  };

  const blogCategoryOptions = [
    { value: "Technology", label: "Technology" },
    { value: "Marketing", label: "Marketing" },
    { value: "Business", label: "Business" },
    { value: "Health", label: "Health" },
    { value: "Lifestyle", label: "Lifestyle" },
  ];

  const handleSave = async () => {
    console.log("Form submitted:", formData);
    if (
      !formData.postTitle ||
      !formData.postCategory ||
      !formData.postDescription
    ) {
      await alert("Please fill in all required fields.");
      return;
    }

    // For editing, image is optional if already exists
    if (!isEditing && !formData.postImage) {
      await alert("Please upload a blog image.");
      return;
    }

    const body = {
      data: {
        title: formData.postTitle,
        category: formData.postCategory,
        content: formData.postDescription,
        isPublished: true,
      },
    };

    // Only include image in form data if it's provided
    if (formData.postImage) {
      body.blog = formData.postImage;
    }

    const finalBody = convertToFormData(body);
    
    try {
      let response;
      
      if (isEditing) {
        response = await dispatch(updateBlog({ data: finalBody, id: blogId }));
        if (updateBlog.fulfilled.match(response)) {
          console.log("✅ Blog Updated Successfully:", response.payload);
          await alert("Blog updated successfully.");
          navigate("/blogs");
        } else if (updateBlog.rejected.match(response)) {
          const errorMsg = response.payload || "Failed to update blog.";
          console.error("❌ Update Failed:", errorMsg);
          await alert(errorMsg);
        }
      } else {
        response = await dispatch(saveBlog(finalBody));
        if (saveBlog.fulfilled.match(response)) {
          console.log("✅ Blog Created Successfully:", response.payload);
          handleReset();
          await alert("Blog created successfully.");
          navigate("/blogs");
        } else if (saveBlog.rejected.match(response)) {
          const errorMsg = response.payload || "Failed to create blog.";
          console.error("❌ Create Failed:", errorMsg);
          await alert(errorMsg);
        }
      }
    } catch (error) {
      console.error(`❌ Error ${isEditing ? 'updating' : 'creating'} blog:`, error);
      await alert(`Failed to ${isEditing ? 'update' : 'create'} blog.`);
    }
  };

  return (
    <SpaceWrapper>
      <div className="w-full flex-1 flex flex-col gap-4 p-4 pb-20 md:pb-4 relative max-w-[2000px]">
        <div className="flex items-center gap-2">
          <BsArrowLeftShort
            onClick={() => navigate("/blogs")}
            className="text-4xl lg:text-5xl text-secondary/70 hover:bg-secondary/5 rounded-xl font-nunito font-bold cursor-pointer"
          />
          <h1 className="text-headline4 lg:text-headline3 font-nunito text-secondary/90 font-bold">
            {isEditing ? "Edit Blog" : "Create Blog"}
          </h1>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 lg:gap-4">
          <InputField
            label="Post Title"
            placeholder="Enter Post Title"
            value={formData.postTitle}
            onChange={(value) => handleChange("postTitle", value)}
          />
          <DropDownInput
            label="Post Category"
            placeholder="Select Post Category"
            options={blogCategoryOptions}
            value={formData.postCategory}
            onChange={(value) => handleChange("postCategory", value)}
          />
          <FileUpload 
            value={formData.postImage} 
            handleChange={handleChange} 
            label={isEditing ? "Update Image (Optional)" : "Upload Image"}
          />
          <div className="flex flex-row gap-2 justify-start items-end">
            <Button
              size="md"
              variant="outline"
              onClick={handleReset}
              className="h-12"
            >
              {isEditing ? "Cancel" : "Reset"}
            </Button>
            <Button
              variant="default"
              size="md"
              className="h-12 !px-5"
              onClick={handleSave}
            >
              {isEditing ? "Update" : "Save"}
            </Button>
          </div>
        </div>
        <MarkdownEditor
          value={formData.postDescription}
          setValue={(value) => handleChange("postDescription", value)}
        />
      </div>
      <AlertDialog />
    </SpaceWrapper>
  );
};

export default CreateBlogs;
